import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Modal } from '../../molecules/Modal';
import { programService } from '../../../services/programService';

const ModalContent = styled.div`
  padding: 24px;
`;

const SessionHeader = styled.div`
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #f0f0f0;
`;

const SessionTitle = styled.h2`
  font-size: 20px;
  color: #333;
  margin: 0 0 12px;
`;

const SessionInfo = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const InfoLabel = styled.span`
  font-size: 13px;
  color: #666;
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 15px;
  color: #333;
  font-weight: 600;
`;

const TrialsSection = styled.div`
  margin-top: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  color: #333;
  margin: 0 0 16px;
  font-weight: 600;
`;

const TrialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
`;

const TrialCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px 8px;
  border-radius: 8px;
  background-color: ${props => {
    switch (props.$status) {
      case 'success': return '#d4edda';
      case 'failure': return '#f8d7da';
      case 'helped_success': return '#fff3cd';
      case 'not_performed': return '#e9ecef';
      default: return '#f8f9fa';
    }
  }};
  border: 2px solid ${props => {
    switch (props.$status) {
      case 'success': return '#28a745';
      case 'failure': return '#dc3545';
      case 'helped_success': return '#ffc107';
      case 'not_performed': return '#6c757d';
      default: return '#dee2e6';
    }
  }};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const TrialNumber = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const TrialStatus = styled.div`
  font-size: 11px;
  font-weight: 500;
  color: ${props => {
    switch (props.$status) {
      case 'success': return '#155724';
      case 'failure': return '#721c24';
      case 'helped_success': return '#856404';
      case 'not_performed': return '#495057';
      default: return '#666';
    }
  }};
  text-align: center;
`;

const StatusLegend = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 8px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendColor = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background-color: ${props => props.$color};
  border: 2px solid ${props => props.$borderColor};
`;

const LegendLabel = styled.span`
  font-size: 13px;
  color: #666;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 16px;
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: #999;
  font-size: 16px;
`;

const getStatusLabel = (status) => {
  switch (status) {
    case 'success': return '성공';
    case 'failure': return '실패';
    case 'helped_success': return '도움받아 성공';
    case 'not_performed': return '미수행';
    default: return '알 수 없음';
  }
};

const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'short'
    });
  } catch {
    return '-';
  }
};

const SessionDetailModal = ({ isOpen, onClose, session }) => {
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && session) {
      fetchTrials();
    }
  }, [isOpen, session]);

  const fetchTrials = async () => {
    try {
      setLoading(true);
      const trialsData = await programService.getSessionTrials(session.id);
      setTrials(trialsData);
    } catch (error) {
      console.error('Failed to fetch trials:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!session) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="세션 상세 기록">
      <ModalContent>
        <SessionHeader>
          <SessionTitle>{formatDate(session.sessionDate)}</SessionTitle>
          <SessionInfo>
            <InfoItem>
              <InfoLabel>총 시도 횟수</InfoLabel>
              <InfoValue>{session.repetitionCount}회</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>성공</InfoLabel>
              <InfoValue style={{ color: '#28a745' }}>{session.successCount}회</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>실패</InfoLabel>
              <InfoValue style={{ color: '#dc3545' }}>{session.failureCount}회</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>성공률</InfoLabel>
              <InfoValue style={{ color: '#007bff' }}>
                {((session.successCount / session.repetitionCount) * 100).toFixed(0)}%
              </InfoValue>
            </InfoItem>
          </SessionInfo>
          {session.notes && (
            <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
              <InfoLabel>메모:</InfoLabel>
              <div style={{ marginTop: '4px' }}>{session.notes}</div>
            </div>
          )}
        </SessionHeader>

        <TrialsSection>
          <SectionTitle>시도별 수행 기록</SectionTitle>

          <StatusLegend>
            <LegendItem>
              <LegendColor $color="#d4edda" $borderColor="#28a745" />
              <LegendLabel>성공</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendColor $color="#f8d7da" $borderColor="#dc3545" />
              <LegendLabel>실패</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendColor $color="#fff3cd" $borderColor="#ffc107" />
              <LegendLabel>도움받아 성공</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendColor $color="#e9ecef" $borderColor="#6c757d" />
              <LegendLabel>미수행</LegendLabel>
            </LegendItem>
          </StatusLegend>

          {loading && <LoadingMessage>시도 기록을 불러오는 중...</LoadingMessage>}

          {!loading && trials.length === 0 && (
            <EmptyMessage>시도 기록이 없습니다.</EmptyMessage>
          )}

          {!loading && trials.length > 0 && (
            <TrialsGrid>
              {trials.map((trial) => (
                <TrialCard key={trial.id} $status={trial.status}>
                  <TrialNumber>#{trial.trialNumber}</TrialNumber>
                  <TrialStatus $status={trial.status}>
                    {getStatusLabel(trial.status)}
                  </TrialStatus>
                </TrialCard>
              ))}
            </TrialsGrid>
          )}
        </TrialsSection>
      </ModalContent>
    </Modal>
  );
};

export default SessionDetailModal;
